import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DecommissioningReportContext from '@totem/components/salesOrders/decommissioning/DecommissioningReportContext';
import {
  NotOnContractItem,
  ObjectReference,
  REASON_EXCEEDS_QUANTITY,
  reasonLabels,
} from '@totem/components/salesOrders/decommissioning/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const styles = {
  stacked: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
};

// Sales order line ids are `${salesOrderNumber}_${lineNumber}`
const getSalesOrderNumber = (salesOrderLineId: string) =>
  salesOrderLineId.substring(0, salesOrderLineId.lastIndexOf('_'));

const DecommissioningReportTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, reportData, searchText } = useContext(
    DecommissioningReportContext,
  );

  const [pageSize, setPageSize] = useState<number>(10);

  const filteredData = useMemo(() => {
    if (!searchText) {
      return reportData ?? [];
    }
    const lower = searchText.toLowerCase();
    return (reportData ?? []).filter(
      (item) =>
        item.name.toLowerCase().includes(lower) ||
        (item.devices ?? []).some((device) =>
          device.name.toLowerCase().includes(lower),
        ),
    );
  }, [reportData, searchText]);

  const organizationFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      if (item.organization?.id) {
        seen.set(item.organization.id, item.organization.name);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const buildingFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      if (item.building?.id) {
        seen.set(item.building.id, item.building.name || 'Unassigned');
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const serviceFilters = useMemo(() => {
    const seen = new Set<string>();
    for (const item of reportData ?? []) {
      if (item.service) {
        seen.add(item.service);
      }
    }
    return Array.from(seen)
      .map((svc) => ({ text: svc, value: svc }))
      .sort((compA, compB) => compA.text.localeCompare(compB.text));
  }, [reportData]);

  const reasonFilters = Object.entries(reasonLabels).map(([val, txt]) => ({
    text: txt,
    value: val,
  }));

  const renderDevice = (record: NotOnContractItem, device: ObjectReference) => {
    if (
      device.name !== '' &&
      device.id !== EMPTY_ID &&
      record.organization?.id === userProfile.organization.id
    ) {
      return (
        <Link
          key={device.id}
          to={`/dashboard/devices/${device.id}`}
          target={device.id}
        >
          {device.name}
        </Link>
      );
    }
    return <span key={device.id}>{device?.name ?? ''}</span>;
  };

  const columns: ColumnProps<NotOnContractItem>[] = [
    {
      title: 'Reason',
      key: 'reason',
      filters: reasonFilters,
      onFilter: (value, record) => record.reason === value,
      sorter: (compA, compB) => compA.reason.localeCompare(compB.reason),
      render: (_, record) => (
        <span>{reasonLabels[record.reason] ?? record.reason}</span>
      ),
    },
    {
      title: 'Service',
      key: 'service',
      filters: serviceFilters,
      onFilter: (value, record) => record.service === value,
      sorter: (compA, compB) => compA.service.localeCompare(compB.service),
      render: (_, record) => <span>{record.service}</span>,
    },
    {
      title: 'Device Name',
      key: 'name',
      sorter: (compA, compB) => compA.name.localeCompare(compB.name),
      render: (_, record) => {
        if (record.reason === REASON_EXCEEDS_QUANTITY) {
          return (
            <div style={styles.stacked}>
              {(record.devices ?? []).map((device) =>
                renderDevice(record, device),
              )}
            </div>
          );
        }
        return renderDevice(record, { id: record.id, name: record.name });
      },
    },
    {
      title: 'Type',
      key: 'type',
      sorter: (compA, compB) => compA.type.localeCompare(compB.type),
      render: (_, record) => <span>{record.type}</span>,
    },
    {
      title: 'Vendor',
      key: 'vendor',
      sorter: (compA, compB) => compA.vendor.localeCompare(compB.vendor),
      render: (_, record) => <span>{record.vendor}</span>,
    },
    {
      title: 'Delivered / SO Qty',
      key: 'quantity',
      sorter: (compA, compB) =>
        compA.deliveredCount -
        compA.contractedQuantity -
        (compB.deliveredCount - compB.contractedQuantity),
      render: (_, record) =>
        record.reason === REASON_EXCEEDS_QUANTITY ? (
          <span>{`${record.deliveredCount} / ${record.contractedQuantity}`}</span>
        ) : (
          <span />
        ),
    },
    {
      title: 'Sales Order Lines',
      key: 'salesOrderLines',
      render: (_, record) => (
        <div style={styles.stacked}>
          {(record.salesOrderLines ?? []).map((salesOrderLine) => (
            <Link
              key={salesOrderLine}
              to={`/dashboard/salesOrder/${getSalesOrderNumber(salesOrderLine)}`}
              target={salesOrderLine}
            >
              {salesOrderLine}
            </Link>
          ))}
        </div>
      ),
    },
    {
      title: 'Organization',
      key: 'organization',
      filters: organizationFilters,
      onFilter: (value, record) => record.organization?.id === value,
      sorter: (compA, compB) =>
        (compA.organization?.name ?? '').localeCompare(
          compB.organization?.name ?? '',
        ),
      render: (_, record) => <span>{record.organization?.name ?? ''}</span>,
    },
    {
      title: 'Building',
      key: 'building',
      filters: buildingFilters,
      onFilter: (value, record) => record.building?.id === value,
      sorter: (compA, compB) =>
        (compA.building?.name ?? '').localeCompare(compB.building?.name ?? ''),
      render: (_, record) => {
        if (
          record.building.name !== '' &&
          record.building.id !== EMPTY_ID &&
          record.organization?.id === userProfile.organization.id
        ) {
          return (
            <Link
              to={`/dashboard/buildings/${record.building.id}`}
              target={record.building.id}
            >
              {record.building.name}
            </Link>
          );
        }
        return <span>{record.building?.name ?? ''}</span>;
      },
    },
  ];

  return (
    <IBTable
      rowKey={(record) => `${record.reason}-${record.service}-${record.id}`}
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
    />
  );
};

export default DecommissioningReportTable;
